import {weakMemo, singleMemo} from '@packages/utilities/memo';
import {omit} from '@packages/utilities/key-map';
import {Plugins, TypeImplementation, SiteModules} from '@packages/systems/wfdl';
import {plugin as Builtin} from '@packages/systems/plugins/Builtin';
import corePlugins from '@packages/shared/render/corePlugins';
import type {Collections} from '@packages/systems/dynamo/records/CollectionRecord';
import {make__SitePlugin} from '@packages/systems/core/plugins';
import type {BetaFeatureFlags} from '@packages/systems/core-constants';

import {
  getCollectionIds,
  makeCollectionListPropTypes,
} from '@packages/shared/render/plugins/Dynamo';
import {reportBugOnce} from '@packages/utilities/reporter';

const buildSitePlugin = singleMemo((timezone: string) =>
  weakMemo(
    (otherDataTypes: {[key: string]: TypeImplementation.TypeImplementation}) =>
      weakMemo(
        (siteModuleDependencies: SiteModules.SiteModuleDependencyMap) => {
          return make__SitePlugin({
            timezone,
            otherDataTypes,
            siteModuleDependencies,
          });
        }
      )
  )
);

const buildBasePlugins = weakMemo((betas: BetaFeatureFlags) => {
  // We currently import every plugin as part of `corePlugins`, however, we want
  // to make specific plugins only available in Designer when their associated
  // beta flag is enabled. Below, we omit plugins from `corePlugins` when their
  // beta isn't enabled.
  const pluginsToOmit = [
    {beta: betas.lab, pluginNames: ['Lab', 'PrivilegedLab']},
  ].reduce<Array<string>>((omittedPluginNames, {beta, pluginNames}) => {
    if (beta) {
      return omittedPluginNames;
    }
    return omittedPluginNames.concat(pluginNames);
  }, []);

  const plugins = omit(pluginsToOmit)(corePlugins);
  return {...plugins, Builtin};
});

const combinePlugins = weakMemo(
  (basePlugins: {Builtin: Plugins.PrivilegedPluginDeclaration}) =>
    weakMemo((sitePlugin: Plugins.PrivilegedPluginDeclaration) => {
      return {
        ...basePlugins,
        __SitePlugin: sitePlugin,
      };
    })
);

export const initBootstrapPlugins = (
  betas: BetaFeatureFlags,
  collections: Collections,
  timezone: string,
  siteModuleDependencies: SiteModules.SiteModuleDependencyMap
) => {
  const basePlugins = buildBasePlugins(betas);
  const collectionListPropsTypes = makeCollectionListPropTypes(
    getCollectionIds(collections)
  );
  const sitePlugin = buildSitePlugin(timezone)(collectionListPropsTypes)(
    siteModuleDependencies
  );

  const allPlugins = combinePlugins(basePlugins)(sitePlugin);

  try {
    return Plugins.bootstrap(allPlugins);
  } catch (err: any) {
    reportBugOnce(
      'DesignerReducerBootstrapPlugin',
      'Could not bootstrap plugins',
      {
        team: 'build-canvas',
        ticket: 'https://webflow.atlassian.net/browse/CANV-2383',
        message: err.message,
        stack: err.stack,
      }
    );
    throw err;
  }
};
